/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useLogger } from '@airasia-rn/core-logger';
import { useAuthToken } from './useAuth';
import { hasJwtExpired } from '@airasia-rn/account/managers';

import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import { LowFareRangeParams, QueryType } from '../types/lowFare.types';
import { flightKeys } from './flights';
import { useLowFareApiService } from './useLowFareScroller';
import { useFlightConfig } from '../config/FlightConfigProvider';
import { getApiPlatform } from '../util/utils';

const LOW_FARE_STALE_TIME = 5 * 60 * 1000;
const LOW_FARE_GC_TIME = 15 * 60 * 1000;
const LOW_FARE_MAX_RETRIES = 1;

/**
 * A 410 (Gone) means the requested fare range/route is permanently unavailable, not a transient
 * failure - retrying it wastes a request and delays surfacing the real state to the user.
 */
export function shouldRetryLowFareQuery(
  failureCount: number,
  error: unknown,
): boolean {
  const status = (error as { status?: number } | undefined)?.status;
  if (status === 410) return false;
  return failureCount < LOW_FARE_MAX_RETRIES;
}

function buildLowFareQueryOptions(args: {
  queryParams: QueryType;
  authToken: string;
  userType: string;
  apiService: ReturnType<typeof useLowFareApiService>;
  authTokenQuery: ReturnType<typeof useAuthToken>;
}) {
  const { queryParams, authToken, userType, apiService, authTokenQuery } = args;

  return {
    queryKey: [
      ...flightKeys.lowFare,
      queryParams.beginDate,
      queryParams.endDate,
      queryParams.departStation,
      queryParams.arrivalStation,
      queryParams.currency,
    ] as const,

    staleTime: LOW_FARE_STALE_TIME,
    gcTime: LOW_FARE_GC_TIME,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const freshToken =
        authTokenQuery.data && !hasJwtExpired(authTokenQuery.data)
          ? authTokenQuery.data
          : await authTokenQuery.refetch().then((res) => res.data);

      return apiService.fetchLowFare(
        queryParams,
        queryParams.channelHash,
        freshToken || authToken,
        userType,
      );
    },
    retry: shouldRetryLowFareQuery,
  };
}

export function useLowFareRange(params: LowFareRangeParams) {
  const {
    queryParams,
    authToken,
    userType,
    screenName = getApiPlatform(),
  } = params;

  const apiService = useLowFareApiService();
  const authTokenQuery = useAuthToken(screenName);
  const { logError } = useLogger();

  const enabled = Boolean(
    authToken &&
      queryParams.beginDate &&
      queryParams.endDate &&
      queryParams.departStation &&
      queryParams.arrivalStation,
  );

  const mergedQueryParams: QueryType = {
    ...queryParams,
    channelHash: queryParams.channelHash,
  };

  const query = useQuery({
    ...buildLowFareQueryOptions({
      queryParams: mergedQueryParams,
      authToken,
      userType,
      apiService,
      authTokenQuery,
    }),
    enabled,
  });

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe the query result instead of hooking into the query itself.
  useEffect(() => {
    if (!query.isError || !screenName) return;

    const { errorType, errorCode, errorDescription, errorMessage } =
      classifyServiceError(query.error);

    logError(
      apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      errorTrackingEvent({
        errorType,
        errorCode,
        errorDescription,
        errorMessage,
        screenName,
        service: apiErrorMeta.SERVICE_NAMES.LFC,
      }),
    );
  }, [query.isError, query.error, screenName, logError]);

  return query;
}

/**
 * Date helpers (API expects dd/MM/yyyy, no locale)
 */
function formatToDDMMYYYY(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function maxDate(a: Date, b: Date): Date {
  return a.getTime() > b.getTime() ? a : b;
}

/**
 * Returns a month range in dd/MM/yyyy, clamped so beginDate is never in the past.
 * - If the entire month is in the past, returns { isValid: false }
 * - Else returns { isValid: true, beginDate, endDate }
 */
export function getMonthRangeClamped(month: string, now: Date = new Date()) {
  // month = "2025-12"
  const [year, monthIndex] = month.split('-').map(Number);

  // monthIndex is 1-based in input "YYYY-MM"
  const monthStart = new Date(year, monthIndex - 1, 1);
  const monthEnd = new Date(year, monthIndex, 0);

  const today = startOfDay(now);

  // Whole month is in the past -> skip
  if (monthEnd.getTime() < today.getTime()) {
    return { isValid: false as const };
  }

  // Clamp start so it's never in the past
  const clampedStart = maxDate(monthStart, today);

  return {
    isValid: true as const,
    beginDate: formatToDDMMYYYY(clampedStart),
    endDate: formatToDDMMYYYY(monthEnd),
  };
}

/**
 * Multiple months hook
 */
export function useLowFareMonths(
  months: string[],
  queryParams: QueryType,
  authToken: string,
  userType: string,
  screenName?: string,
) {
  const apiService = useLowFareApiService();
  const flightConfig = useFlightConfig();
  const channelHash = flightConfig.config.channelHash;

  const authTokenQuery = useAuthToken(screenName);
  const { logError } = useLogger();

  const isDistress = !!flightConfig.config.isDistressflights;

  const baseEnabled = Boolean(
    !isDistress &&
      authToken &&
      queryParams.departStation &&
      queryParams.arrivalStation,
  );

  const results = useQueries({
    queries: months.map((month) => {
      const range = getMonthRangeClamped(month);
      const mergedQueryParams: QueryType = {
        ...queryParams,
        channelHash,
        beginDate: range.beginDate,
        endDate: range.endDate,
      };

      return {
        ...buildLowFareQueryOptions({
          queryParams: mergedQueryParams,
          authToken,
          userType,
          apiService,
          authTokenQuery,
        }),
        enabled: baseEnabled && range.isValid,
      };
    }),
  });

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe each query result instead of hooking into the query
  // itself - a stable per-query signature keeps the effect from re-running
  // every render just because the `results` array identity changed.
  const resultsErrorSignature = useMemo(
    () =>
      results
        .map((result) => `${result.status}:${result.dataUpdatedAt}`)
        .join('|'),
    [results],
  );

  useEffect(() => {
    if (!screenName) return;

    results.forEach((result) => {
      if (!result.isError) return;

      const { errorType, errorCode, errorDescription, errorMessage } =
        classifyServiceError(result.error);

      logError(
        apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
        errorTrackingEvent({
          errorType,
          errorCode,
          errorDescription,
          errorMessage,
          screenName,
          service: apiErrorMeta.SERVICE_NAMES.LFC,
        }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsErrorSignature, screenName, logError]);

  return results;
}
